/**
 * PredictionCard, PredictionLegRow, and ErrorCard - Settled AI prediction display components
 */
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { Info } from 'lucide-react';
import type { ReactElement } from 'react';
import type {
  SettledPredictionDetail,
  SettledPredictionLeg,
  SettlementError,
} from '../../hooks/useLabDashboard';

function PredictionLegRow({ leg }: { leg: SettledPredictionLeg }): ReactElement {
  const getStatusInfo = () => {
    if (leg.hit === true) {
      return { color: 'text-theme-success', bg: 'bg-theme-success', label: '✓' };
    }
    if (leg.hit === false) {
      return { color: 'text-theme-danger', bg: 'bg-theme-danger', label: '✗' };
    }
    return { color: 'text-theme-warning', bg: 'bg-theme-warning', label: '?' };
  };

  const status = getStatusInfo();

  // Format prop display
  const propDisplay =
    leg.overUnder && leg.line
      ? `${leg.overUnder} ${leg.line} ${formatStatLabel(leg.propType)}`
      : `${formatStatLabel(leg.propType)} ${leg.line || ''}`;

  // Format actual value
  const actualDisplay = leg.actualValue !== null ? parseFloat(leg.actualValue).toFixed(1) : '-';

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      {/* Status indicator */}
      <div
        className={`w-6 h-6 rounded-full ${status.bg} flex items-center justify-center shrink-0`}
      >
        <span className="text-white text-xs font-bold">{status.label}</span>
      </div>

      {/* Player and prop info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white truncate">{leg.playerName || 'Unknown'}</span>
          <span className="text-xs text-theme-text-tertiary shrink-0">({leg.team})</span>
        </div>
        <div className="text-sm text-theme-text-secondary">{propDisplay}</div>
      </div>

      {/* Actual value */}
      <div className="text-right shrink-0">
        <div className={`text-sm font-medium ${status.color}`}>Actual: {actualDisplay}</div>
      </div>
    </div>
  );
}

export function PredictionCard({
  prediction,
}: {
  prediction: SettledPredictionDetail;
}): ReactElement {
  const totalLegs = prediction.legs.length;
  const allWon = prediction.legsWon === totalLegs;
  const allLost = prediction.legsLost === totalLegs;

  // Determine overall result color
  const resultColors = allWon
    ? 'bg-theme-success/10 text-theme-success border-theme-success'
    : allLost
      ? 'bg-theme-danger/10 text-theme-danger border-theme-danger'
      : 'bg-theme-warning/10 text-theme-warning border-theme-warning';

  const resultLabel = allWon ? 'ALL HIT' : allLost ? 'ALL MISSED' : 'MIXED';

  // Format date
  const dateStr = prediction.gameDate
    ? new Date(prediction.gameDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <div className="bg-theme-surface rounded-lg border border-purple-700/50 overflow-hidden">
      {/* Prediction Header */}
      <div className="p-4 border-b border-theme-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${resultColors}`}>
              {resultLabel}
            </span>
            <span className="text-xs text-purple-400 bg-purple-900/30 px-2 py-0.5 rounded border border-purple-700/50">
              {prediction.aiTier}
            </span>
            {dateStr && <span className="text-xs text-theme-text-tertiary">{dateStr}</span>}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-theme-success">{prediction.legsWon} hit</span>
            <span className="text-theme-text-tertiary">/</span>
            <span className="text-theme-danger">{prediction.legsLost} missed</span>
          </div>
        </div>
      </div>

      {/* Legs */}
      <div className="divide-y divide-gray-700/50">
        {prediction.legs.map((leg, idx) => (
          <PredictionLegRow key={idx} leg={leg} />
        ))}
      </div>
    </div>
  );
}

export function ErrorCard({ error }: { error: SettlementError }): ReactElement {
  return (
    <div className="p-3 bg-theme-danger/20 border border-red-800/50 rounded-lg">
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 text-theme-danger mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-red-300 font-medium">{error.error}</p>
          {/* Show affected players if available */}
          {error.affectedPlayers && error.affectedPlayers.length > 0 ? (
            <div className="mt-2 space-y-1">
              {error.affectedPlayers.map((player, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <span className="text-red-200/80 font-medium">{player.player}</span>
                  {player.team && <span className="text-red-200/50">({player.team})</span>}
                  <span className="text-red-200/40">·</span>
                  <span className="text-red-200/60">{player.prop}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-red-200/50 mt-1">
              {error.legsAffected} leg{error.legsAffected !== 1 ? 's' : ''} affected
            </p>
          )}
          <p className="text-xs text-red-200/40 mt-2">Game ID: {error.gameId.slice(0, 12)}...</p>
        </div>
      </div>
    </div>
  );
}
